/** @jsxImportSource @emotion/react */
import { PinInput, PinInputField } from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { AuthAPI } from "../../../api/AuthUser";
import StyledButton from "../../StyledButton";
import { useModal } from "../Modal";

const ModalBody = styled.div`
  width: 390px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #eceeee;

  margin: 16px -24px 0px;
`;

const Wrapper = styled.div`
  width: 100%;
  height: fit-content;

  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;

  padding: 32px 24px;

  > div + div {
    margin-top: 24px;
  }
`;

const Buttons = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  border-top: 1px solid #eceeee;
  padding: 24px 24px 0px;
`;

const InputLabel = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
  margin-bottom: 12px;
`;

const ForgotPassword = styled.a`
  margin-top: 32px;

  cursor: pointer;
  color: #fb972e;

  &:hover {
    text-decoration: underline;
  }
`;

const SingleInputContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
`;

const NextButtonContainer = styled.div`
  width: 100%;
  margin: 40px 0 0 0;
`;

const ErrorText = styled.div`
  margin-top: -35px;
  margin-left: 22px;
  color: #c00000;
  size: 14px;

  @media (max-width: 740px) {
    margin-left: 12px;
  }

  @media (max-width: 650px) {
    margin-left: 5px;
  }
`;

const StyledPinInputField = styled(PinInputField)`
  width: 60px;
  height: 70px;
  background-color: #ffffff;

  font-size: 24px;

  border: 1px solid ${(props) => (props.error ? "#C00000" : "#cccccc")};

  :focus {
    border-color: ${(props) => (props.error ? "#C00000" : "#fb972e ")};
    box-shadow: 0 0 0 1px ${(props) => (props.error ? "#C00000" : "#fb972e ")};
  }
`;

export default function ChangePassword(): JSX.Element {
  const oldPasswordInput = useRef(null);
  const newPasswordInput = useRef(null);
  const confirmNewPasswordInput = useRef(null);
  const modal = useModal();
  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState<string>("");
  const [data, setData] = useState<{
    oldPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }>({
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  function handlePasswordKeyPressed(
    value: string,
    key: "oldPassword" | "newPassword" | "confirmNewPassword"
  ) {
    setData({ ...data, [key]: value });
    setError("");

    if (value.length === 4) {
      switch (key) {
        case "oldPassword":
          newPasswordInput.current.focus();
          break;
        case "newPassword":
          confirmNewPasswordInput.current.focus();
          break;
        case "confirmNewPassword":
          if (data.newPassword !== value) {
            setError("Passcode do not match");
            return;
          }
          break;
      }
    }
  }

  async function changePassword() {
    if (data.newPassword !== data.confirmNewPassword) {
      setError("Passcode do not match");
      return;
    }

    if (data.oldPassword === data.newPassword) {
      setError("New passcode cannot be the same as old passcode");
      return;
    }

    setIsLoading(true);

    const isChanged = await AuthAPI.changePassword(
      data.oldPassword,
      data.newPassword
    );

    if (isChanged) {
      modal.popAll();
      toast("Password changed successfully", {
        type: "success",
      });
    } else {
      toast("Error changing password! Try again later!", {
        type: "error",
      });
    }

    setIsLoading(false);
  }

  return (
    <ModalBody>
      <Wrapper>
        <InputLabel>
          <Title>Enter old passcode</Title>
          <SingleInputContainer>
            <PinInput
              placeholder=""
              onChange={(value) =>
                handlePasswordKeyPressed(value, "oldPassword")
              }
              mask
            >
              <StyledPinInputField ref={oldPasswordInput} />
              <StyledPinInputField />
              <StyledPinInputField />
              <StyledPinInputField />
            </PinInput>
          </SingleInputContainer>
        </InputLabel>

        <InputLabel>
          <Title>Enter new passcode</Title>
          <SingleInputContainer>
            <PinInput
              placeholder=""
              onChange={(value) =>
                handlePasswordKeyPressed(value, "newPassword")
              }
              mask
            >
              <StyledPinInputField ref={newPasswordInput} error={!!error} />
              <StyledPinInputField error={!!error} />
              <StyledPinInputField error={!!error} />
              <StyledPinInputField error={!!error} />
            </PinInput>
          </SingleInputContainer>
        </InputLabel>

        <InputLabel>
          <Title>Confirm new passcode</Title>
          <SingleInputContainer>
            <PinInput
              placeholder=""
              onChange={(value) =>
                handlePasswordKeyPressed(value, "confirmNewPassword")
              }
              mask
            >
              <StyledPinInputField
                ref={confirmNewPasswordInput}
                error={!!error}
              />
              <StyledPinInputField error={!!error} />
              <StyledPinInputField error={!!error} />
              <StyledPinInputField error={!!error} />
            </PinInput>
          </SingleInputContainer>
          {error && (
            <NextButtonContainer>
              <ErrorText>{error}</ErrorText>
            </NextButtonContainer>
          )}
        </InputLabel>
      </Wrapper>

      <Buttons>
        <StyledButton
          onClick={() => modal.popAll()}
          inverted
          css={css`
            width: 80px;
            margin-right: 10px;
          `}
        >
          Cancel
        </StyledButton>
        <StyledButton
          css={css`
            width: 80px;
          `}
          onClick={changePassword}
          disable={
            data.oldPassword.length !== 4 ||
            data.newPassword.length !== 4 ||
            data.confirmNewPassword.length !== 4 ||
            !!error
          }
          isLoading={isLoading}
        >
          Save
        </StyledButton>
      </Buttons>
    </ModalBody>
  );
}
